import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ChatContact, ChatMessage, ChatPeer } from './chat.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ChatApiService {
  constructor(private api: ApiService) {}

  getContacts(): Observable<ChatPeer[]> {
    return this.api.get('/chat/contacts').pipe(
      map(res => this.unwrap(res).map((c: any) => this.mapPeer(c)))
    );
  }

  getConversations(): Observable<ChatContact[]> {
    return this.api.get('/chat/conversations').pipe(
      map(res => this.unwrap(res).map((c: any) => this.mapConversation(c)))
    );
  }

  getMessages(peerId: any, beforeId?: any, limit: number = 30): Observable<ChatMessage[]> {
    const params: any = { limit };
    if (beforeId != null) { params.before = beforeId; }
    return this.api.get(`/chat/conversations/${peerId}/messages`, params).pipe(
      map(res => this.unwrap(res).map((m: any) => this.mapMessage(m)))
    );
  }

  sendMessage(peerId: any, body: string, clientId: string): Observable<ChatMessage> {
    return this.api.post(`/chat/conversations/${peerId}/messages`, { body, clientId }).pipe(
      map(res => this.mapMessage(this.unwrapOne(res)))
    );
  }

  sendImage(peerId: any, image: string, clientId: string, caption: string = ''): Observable<ChatMessage> {
    return this.api.post(`/chat/conversations/${peerId}/attachments`, { image, clientId, caption }).pipe(
      map(res => this.mapMessage(this.unwrapOne(res)))
    );
  }

  markRead(peerId: any, upToMessageId: any): Observable<any> {
    return this.api.post(`/chat/conversations/${peerId}/read`, { upToMessageId });
  }

  getUnreadCount(): Observable<{ total: number; byPeer: { [id: string]: number } }> {
    return this.api.get('/chat/unread-count').pipe(
      map(res => {
        const d = this.unwrapOne(res) || {};
        return { total: Number(d.total) || 0, byPeer: d.byPeer || d.by_peer || {} };
      })
    );
  }

  // ===== mapowanie odpowiedzi (tolerancyjne na camel/snake case) =====

  // ID zalogowanego ustawiane z ChatService (do wyliczenia `mine`).
  public currentUserId: any = null;

  // Backend zwraca avatar jako ścieżkę względem hosta API (jak w dashboard-header) —
  // bez prefiksu <img> celowałby w domenę frontu i obrazek by się nie ładował.
  private readonly avatarBase = environment.api_url.replace(/\/api$/, '');

  private normalizeAvatar(avatar: any): string | null {
    if (!avatar || typeof avatar !== 'string') { return null; }
    if (/^(https?:)?\/\//i.test(avatar) || avatar.indexOf('data:') === 0) { return avatar; }
    return this.avatarBase + (avatar.charAt(0) === '/' ? '' : '/') + avatar;
  }

  private mapPeer(c: any): ChatContact {
    if (!c) { return { id: null, name: '' }; }
    const peer = c.peer || c;
    return {
      id: peer.id ?? peer.userId ?? peer.user_id,
      name: peer.name || `${peer.firstName || peer.first_name || ''} ${peer.lastName || peer.last_name || ''}`.trim(),
      avatar: this.normalizeAvatar(peer.avatar),
      online: !!(peer.online ?? peer.isOnline ?? peer.is_online),
      lastLogin: peer.lastLogin,
      lastLogout: peer.lastLogout,
    };
  }

  private mapConversation(c: any): ChatContact {
    const base = this.mapPeer(c);
    return {
      ...base,
      email: (c.peer && (c.peer.email)) || c.email,
      lastMessage: c.lastMessage || c.last_message || c.preview || '',
      time: c.time || c.updatedAt || c.updated_at || '',
      unread: Number(c.unread ?? c.unreadCount ?? c.unread_count) || 0,
    };
  }

  private mapMessage(m: any): ChatMessage {
    if (!m) { return { id: null, mine: true, body: '', createdAt: Date.now() }; }
    const senderId = m.senderId ?? m.sender_id ?? m.sender;
    const created = m.createdAt || m.created_at;
    return {
      id: m.id,
      mine: this.currentUserId != null && String(senderId) === String(this.currentUserId),
      body: m.body || m.text || m.message || '',
      createdAt: created ? new Date(created).getTime() : Date.now(),
      attachmentUrl: m.attachmentUrl || m.attachment_url || undefined,
      attachmentType: m.attachmentType || m.attachment_type || undefined,
    };
  }

  private unwrap(res: any): any[] {
    const d = (res && res.data !== undefined) ? res.data : res;
    return Array.isArray(d) ? d : (Array.isArray(d && d.items) ? d.items : []);
  }

  private unwrapOne(res: any): any {
    return (res && res.data !== undefined) ? res.data : res;
  }
}
